import { useContext, useEffect, useRef, useState } from "react"
import AdHocCommands from "@/api/adHocCommands"
import {
  LatestDeliveryOrder,
  parseLatestDeliveryOrder,
} from "@/components/maps/orderDetails/OrderDetailsDialog"
import { isSltExportEnabledVhost } from "@/lib/sltExportHostRules"
import { sameStops, type RouteStop } from "@/reducers/routes2gSlice"
import { XmppContext } from "@/stream/xmppClient"

/** Order status is an SLT-export feature. It shows only on those vhosts,
 * for a driver, and only once the route has stops — the same gate as the
 * classic map's order button. Anywhere else the link is absent. */
export const showsOrderStatus = (
  vhost: string | undefined,
  driverJid: string | undefined,
  stops: RouteStop[] | undefined,
) =>
  isSltExportEnabledVhost(vhost) && Boolean(driverJid) && Boolean(stops?.length)

export const useLatestDeliveryOrder = (
  driverJid: string | undefined,
  stops: RouteStop[] | undefined,
  paused: boolean,
) => {
  const { client } = useContext(XmppContext)

  // Only a change to the plan itself (id, position, avoided area — same
  // comparison `upsertRoute2g` uses to decide whether to carry the previous
  // route's tolls forward) should ask for the order again. gps republishes
  // the whole route, live figures and all, on every location update of a
  // driver with a route, so keying this on the `stops` reference itself
  // would re-fetch on every GPS ping.
  const plan = useRef(stops)
  if (
    stops !== plan.current &&
    !(stops && plan.current && sameStops(plan.current, stops))
  ) {
    plan.current = stops
  }
  const planStops = plan.current

  const enabled = showsOrderStatus(client?.jid?.domain, driverJid, planStops)

  const [latest, setLatest] = useState<LatestDeliveryOrder | null>(null)
  useEffect(() => {
    if (paused) return
    if (!client || !driverJid || !enabled) {
      setLatest(null)
      return
    }
    let cancelled = false
    AdHocCommands.showLatestDeliveryOrder(client, driverJid)
      .then((result) => {
        if (!cancelled) setLatest(parseLatestDeliveryOrder(result))
      })
      .catch(() => {
        if (!cancelled) setLatest(null)
      })
    return () => {
      cancelled = true
    }
  }, [client, planStops, driverJid, enabled, paused])

  return { enabled, latest }
}
